import React, { useState } from 'react';
import { Modal, Button, Select } from '@/components/ui';
import { UserPlus, Search, CheckSquare, Square } from 'lucide-react';
import { cn } from '@/utils/cn';

interface AddMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableUsers: any[];
  isLoading: boolean;
  onAdd: (userIds: string[], role: string) => void;
  isMutating: boolean;
}

export function AddMemberModal({ isOpen, onClose, availableUsers, isLoading, onAdd, isMutating }: AddMemberModalProps) {
  const [search, setSearch] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>('member');

  // Reset state when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setSelectedUserIds([]);
      setSearch('');
      setSelectedRole('member');
    }
  }, [isOpen]);

  const filteredUsers = availableUsers.filter(u => 
    u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const toggleUser = (id: string) => {
    setSelectedUserIds(prev => 
      prev.includes(id) ? prev.filter(userId => userId !== id) : [...prev, id]
    );
  };

  const handleAdd = () => {
    if (selectedUserIds.length > 0) {
      onAdd(selectedUserIds, selectedRole);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Thêm thành viên" size="md">
      <div className="p-4 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Tìm kiếm nhân sự..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-md sm:text-sm text-xs outline-none focus:ring-1 focus:ring-emerald-500/20 focus:border-emerald-500/50"
          />
        </div>

        {/* User List */}
        <div className="border border-slate-200 rounded-md h-64 overflow-y-auto bg-slate-50">
          {isLoading ? (
            <div className="p-4 text-center text-slate-400 sm:text-sm text-xs">Đang tải...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-4 text-center text-slate-400 sm:text-sm text-xs italic">Không tìm thấy nhân sự phù hợp.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredUsers.map(user => {
                const isSelected = selectedUserIds.includes(user.id);
                return (
                  <div 
                    key={user.id} 
                    onClick={() => toggleUser(user.id)}
                    className={cn(
                      "flex items-center gap-3 p-3 cursor-pointer transition-colors hover:bg-white",
                      isSelected ? "bg-emerald-50/50" : ""
                    )}
                  >
                    <div className={cn("flex-shrink-0", isSelected ? "text-emerald-600" : "text-slate-300")}>
                      {isSelected ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                    </div>
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs text-slate-500 shrink-0">
                      {user.full_name?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="sm:text-sm text-xs text-slate-900 font-normal truncate">{user.full_name}</p>
                      <p className="text-[11px] text-slate-500 truncate">{user.email || 'N/A'}</p>
                    </div>
                    <span className="text-[11px] px-1.5 py-0.5 bg-slate-100 rounded text-slate-600 shrink-0">
                      {user.role_code}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected Count */}
        {selectedUserIds.length > 0 && (
          <div className="text-xs text-emerald-600">
            Đã chọn {selectedUserIds.length} nhân sự
          </div>
        )}

        {/* Role Select */}
        <div className="space-y-1.5">
          <label className="sm:text-sm text-xs font-normal text-slate-700">Quyền hạn trong team</label>
          <Select
            value={selectedRole}
            onValueChange={setSelectedRole}
            options={[
              { value: 'foreman', label: 'Tổ trưởng' },
              { value: 'vice_foreman', label: 'Tổ phó' },
              { value: 'member', label: 'Thành viên' },
            ]}
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
          <Button variant="outline" onClick={onClose}>Hủy</Button>
          <Button onClick={handleAdd} disabled={selectedUserIds.length === 0 || isMutating} className="bg-emerald-800 hover:bg-emerald-900">
            {isMutating ? 'Đang thêm...' : 'Thêm thành viên'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
